/// <reference path="../../typings/browser.d.ts" />

namespace Application.Services {
    'use strict';

    import IVno = Models.IVno;
    import Vno = Models.Vno;
    import IUser = Models.IUser;
    import IUserGroup = Models.IUserGroup;
    import Utils = Application.Common.Utils;

    export class VnoService {
        public static $name: string = 'vnoService';
        public static $inject: string[] = ['$q', 'backendService', 'stompService', '$rootScope', 'userService'];
        public static VNO_CHANGED: string = 'vnoChangedEvent';

        public vnos: Vno[] = [];

        private backendBaseUrl: string = window.backendUrl;
        private servicePath: string = 'flexauth/';
        private organizationsBaseUrl: string = this.backendBaseUrl + this.servicePath + 'organizations/';
        private vnoBaseUrl: string = this.backendBaseUrl + 'vnos/';

        constructor(private $q: ng.IQService,
                    private backendService: Services.BackendService,
                    private stompService: Services.StompService,
                    private $rootScope: ng.IRootScopeService,
                    private userService: Services.UserService) {
            this.stompService.register(Models.FLEXAUTH_COMPONENT, Models.ALL_OPERATIONS, (flexEvent: Models.IFlexEvent) => {
                switch (flexEvent.operation) {
                    case Models.OPERATION_CREATE_ORGANIZATION:
                        return this.vnoCreatedEvent(flexEvent);
                    case Models.OPERATION_DELETE_ORGANIZATION:
                        return this.vnoDeletedEvent(flexEvent);
                    case Models.OPERATION_SAVE_ORGANIZATION:
                        return this.vnoSavedEvent(flexEvent);
                    case Models.USER_ADDED_TO_GROUP:
                    case Models.USER_ADDED_TO_ROLE:
                    case Models.USER_REMOVED_FROM_ROLE:
                    case Models.USER_CREATED_OPERATION:
                    case Models.USER_REMOVED_FROM_GROUP:
                    case Models.USER_SAVED_OPERATION:
                    case Models.GROUP_CREATED_OPERATION:
                    case Models.GROUP_SAVED_OPERATION:
                    case Models.GROUP_DELETED_OPERATION:
                    case Models.USER_REMOVED_FROM_ORGANIZATION:
                        return this.userOrGroupModifiedEvent(flexEvent);
                    default:
                        return;
                }
            });

            this.$rootScope.$on('vars.isUserLoggedIn', (event: ng.IAngularEvent, arg: boolean) => {
                if (arg) {
                    this.findAll();
                } else {
                    this.cleanServiceData();
                }
            });
        }

        public findAll(forceUpdate: boolean = false): ng.IPromise<Vno[]> {
            const deferred: ng.IDeferred<Vno[]> = this.$q.defer();
            if (!forceUpdate && this.vnos && this.vnos.length > 0) {
                deferred.resolve(this.vnos);
            } else {
                this.backendService.get(this.vnoBaseUrl, false,undefined, false, false)
                    .then((response: ng.IHttpPromiseCallbackArg<Vno[]>) => {
                        this.vnos = response.data;
                        deferred.resolve(response.data);
                    }).catch((reason: ngCustom.IReason) => {
                        deferred.reject(reason);
                    });
            }
            return deferred.promise;
        }

        public findById(id: number | string): ng.IPromise<Vno> {
            if (typeof id === 'string')
                id = parseInt(id, 10);

            const deferred: ng.IDeferred<Vno> = this.$q.defer();
            if (this.vnos && this.vnos.length > 0) {
                const filteredVnos: Vno[] = this.vnos.filter((v: Vno) => v.id === id);
                if (filteredVnos !== undefined && filteredVnos[0] !== undefined) {
                    deferred.resolve(filteredVnos[0]);
                } else {
                    this.backendService.get(this.vnoBaseUrl + id).then((response: ng.IHttpPromiseCallbackArg<Vno>) => {
                        if (response.data !== undefined) {
                            const i: number = this.findVnoIndex(response.data.id);
                            if (i === undefined) {
                                this.vnos.push(response.data);
                            }
                        }
                        deferred.resolve(response.data);
                    });
                }
            } else {
                this.backendService.get(this.vnoBaseUrl + id).then((response: ng.IHttpPromiseCallbackArg<Vno>) => {
                    if (response.data !== undefined) {
                        const i: number = this.findVnoIndex(response.data.id);
                        if (i === undefined) {
                            this.vnos.push(response.data);
                        }
                    }
                    deferred.resolve(response.data);
                });
            }
            return deferred.promise;
        }

        public getVnoLogo(id: number | string): ng.IPromise<string> {
            return this.backendService.get(this.vnoBaseUrl + Number(id) + '/logo')
                .then((response: ng.IHttpPromiseCallbackArg<[{logo: string}]>) => {
                    if (response && response.data && response.data.length > 0)
                        return response.data[0].logo;
                    else
                        return undefined;
            });
        }

        public save(vno: IVno): ng.IPromise<IVno> {
            if (vno.id === undefined) {
                return this.createVno(vno);
            } else {
                return this.updateVno(vno);
            }
        }

        public assignUserToVno(user: Models.User, vno: IVno): ng.IPromise<Models.IUser> {
            return this.backendService
                .put(this.vnoBaseUrl + vno.id + '/users/' + user.id, {})
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser>) => {
                    return result.data;
                });
        }

        public removeUserFromVno(user: Models.IUser, vno: IVno): ng.IPromise<Models.IUser> {
            return this.backendService
                .delete(this.vnoBaseUrl + vno.id + '/users/' + user.id)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser>) => {
                    return result.data;
                });
        }

        public updateUsersFromVno(usersToAdd: Models.IUser[], usersToDelete: Models.IUser[], vno: IVno): ng.IPromise<Models.IUser[]> {
            return this.getVnoUsers(vno).then((users: Models.IUser[]) => {
                const remainingUsers: Models.IUser[] = users.filter((user: Models.IUser) => {
                    return usersToDelete.every((userToDelete: Models.IUser) => user.id !== userToDelete.id);
                });
                const userIds: number[] = remainingUsers.concat(usersToAdd).map((user: Models.IUser) => +user.id);
                return this.backendService
                    .put(this.vnoBaseUrl + vno.id + '/users/edit/list', userIds)
                    .then((result: ng.IHttpPromiseCallbackArg<Models.IUser[]>) => {
                        return result.data;
                    });
            });
        }

        public createUserIntoVno(user: Models.User, vno: IVno): ng.IPromise<Models.IUser> {
            return this.backendService
                .post(this.vnoBaseUrl + vno.id + '/users', user)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser>) => {
                    return result.data;
                });
        }

        public assignAdminToVno(user: Models.User, vno: IVno): ng.IPromise<Models.IUser> {
            return this.backendService
                .put(this.vnoBaseUrl + vno.id + '/admins/' + user.id, {})
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser>) => {
                    return result.data;
                });
        }

        public demoteAdminFromVno(user: Models.User, vno: IVno): ng.IPromise<Models.IUser> {
            return this.backendService
                .delete(this.vnoBaseUrl + vno.id + '/admins/' + user.id)
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser>) => {
                    return result.data;
                });
        }

        public getVnoUsers(vno: IVno): ng.IPromise<Models.IUser[]> {
            return this.backendService
                .get(this.organizationsBaseUrl + vno.id + '/users/')
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser[]>) => {
                    return result.data;
                });
        }

        public getVnoUsersById(id: number): ng.IPromise<Models.IUser[]> {
            return this.backendService
                .get(this.organizationsBaseUrl + id + '/users/')
                .then((result: ng.IHttpPromiseCallbackArg<Models.IUser[]>) => {
                    return result.data;
                });
        }

        public createVnoWithAdmin(vno: Controllers.IAdminVnoModalResponse): ng.IPromise<IVno> {
            return this.backendService
                .post(this.vnoBaseUrl, vno, false, undefined, true)
                .then((result: ng.IHttpPromiseCallbackArg<IVno>) => {
                    return result.data;
                });
        }

        public createVno(vnoAndUser: Controllers.IAdminVnoModalResponse): ng.IPromise<Vno> {
            return this.backendService
                .post(this.vnoBaseUrl, vnoAndUser, false, undefined, true)
                .then((result: ng.IHttpPromiseCallbackArg<IVno>) => {
                    return this.buildVnoObject(result);
                });
        }

        public deleteVno(id: number): ng.IPromise<boolean> {
            return this.backendService
                .delete(this.vnoBaseUrl + id)
                .then((result: ng.IHttpPromiseCallbackArg<boolean>) => {
                    if (result.data) {
                        const index: number = this.findVnoIndex(id);
                        if (index !== undefined) {
                            this.vnos.splice(index, 1);
                        }
                    }
                    return result.data;
                }).catch(e => {
                    return false;
                });
        }

        // public userIsAdmin(userId: number): boolean {
        //     for (const vno of this.vnos) {
        //         for (const adminUser of vno.adminUsers) {
        //             if (adminUser.id === userId) return true;
        //         }
        //     }
        // }

        public async getUserRoles(userId: number): Promise<string[]> {
            for (const vno of await this.findAll(false)) {
                for (const adminUser of vno.adminUsers) {
                    if (adminUser.id === userId) {
                        return adminUser.authorities.map((auth: Models.IAuthority) => auth.authority);
                    }
                }
                for (const user of vno.users) {
                    if (user.id === userId) {
                        return user.authorities.map((auth: Models.IAuthority) => auth.authority);
                    }
                }
            }
            const user: IUser = await this.userService.findById(userId);
            return user.authorities.map((auth: Models.IAuthority) => auth.authority);
        }

        public async isAdminUser(userId: number): Promise<boolean> {
            const roles: string[] = await this.getUserRoles(userId); 
            for (let role of roles) {
                if (role === 'ROLE_ADMIN')
                    return true;
            }
            return false;
        }

        public async isVnoAdmin(userId: number, vnoId?: number): Promise<boolean> {
            const roles: string[] = await this.getUserRoles(userId);
            const vnoIdString: string = vnoId ? `${vnoId}` : ''; 
            return roles.some((role: string) => role.indexOf(`ORG_ADMIN_${vnoIdString}`) !== -1);
        }

        public async createVnoGroup(group: IUserGroup, vnoId: number): Promise<IUserGroup> {
            return this.backendService.post(this.getVnoGroupsUrl(vnoId), group).then((result: ng.IHttpPromiseCallbackArg<IUserGroup>) => {
                return result.data;
            });
        }

        public async editVnoGroup(group: IUserGroup, vnoId: number): Promise<IUserGroup> {
            const editVnoGroupUrl: string = this.getVnoGroupsUrl(vnoId) + '/' + group.id;
            return this.backendService.post(editVnoGroupUrl, group).then((result: ng.IHttpPromiseCallbackArg<IUserGroup>) => {
                return result.data;
            });
        }

        public async removeVnoGroup(vnoId: number, groupId: number): Promise<IUserGroup> {
            const editVnoGroupUrl: string = this.getVnoGroupsUrl(vnoId) + '/' + groupId;
            return this.backendService.delete(editVnoGroupUrl).then((result: ng.IHttpPromiseCallbackArg<IUserGroup>) => {
                return result.data;
            });
        }

        public removeUserFromVnoGroup(vnoId: number, groupId: number, userId: number): ng.IPromise<IUser> {
            return this.backendService
                .delete(`${this.backendBaseUrl}vnos/${vnoId}/groups/${groupId}/users/${userId}`)
                .then((response: ng.IHttpPromiseCallbackArg<IUser>) => {
                    return this.userService.refreshUser(userId).then(() => {
                        return response.data;
                    })
                });
        }

        private getVnoGroupsUrl(vnoId: number): string  {
            return this.vnoBaseUrl + vnoId + '/groups';
        }

        private findByIdBackendDirectRequest(id: number | string): ng.IPromise<Vno> {
            if (typeof id === 'string')
                id = parseInt(id, 10);

            return this.backendService.get(this.vnoBaseUrl + id, false, undefined, false, false).then((response: ng.IHttpPromiseCallbackArg<Vno>) => response.data);
        }

        private updateVno(vno: IVno): ng.IPromise<Vno> {
            return this.backendService
                .put(this.vnoBaseUrl + vno.id, {
                    name: vno.name,
                    id: vno.id,
                    logo: vno.logo ? vno.logo : null, // tslint:disable-line
                })
                .then((result: ng.IHttpPromiseCallbackArg<IVno>) => {
                    return this.buildVnoObject(result);
                });
        }

        private buildVnoObject(result: angular.IHttpPromiseCallbackArg<IVno>): Vno {
            let vno: Vno = new Vno();
            vno.id = result.data.id;
            vno.name = result.data.name;
            vno.logo = result.data.logo;
            return vno;
        }

        private vnoCreatedEvent(flexEvent: Models.IFlexEvent): void {
            this.findById(flexEvent.data.id).then((res: Vno) => {
                const i: number = this.findVnoIndex(res.id);
                if (i !== undefined) {
                    this.vnos[i] = res;
                } else {
                    this.vnos.push(res);
                }
                this.$rootScope.$broadcast(VnoService.VNO_CHANGED, undefined);
            });
        }

        private vnoSavedEvent(flexEvent: Models.IFlexEvent): void {
            if (flexEvent === undefined)
                return;

            this.findByIdBackendDirectRequest(flexEvent.data.id).then((res: Vno) => {
                const i: number = this.findVnoIndex(res.id);
                if (i !== undefined) {
                    this.vnos[i] = res;
                } else {
                    this.vnos.push(res);
                }
                this.$rootScope.$broadcast(VnoService.VNO_CHANGED, undefined);
            });
        }

        private findVnoIndex(id: number | string): number {
            for (let i: number = 0; i < this.vnos.length; i++) {
                if (parseInt('' + this.vnos[i].id, 10) === parseInt('' + id, 10)) {
                    return i;
                }
            }
            return undefined;
        }

        private vnoDeletedEvent(flexEvent: Models.IFlexEvent): void {
            console.log('warning! this event is not being processed!');
            this.$rootScope.$broadcast(VnoService.VNO_CHANGED, undefined);
        }

        private userOrGroupModifiedEvent(flexEvent: Models.IFlexEvent): void {
            this.findAll(true).then(() => {
                this.$rootScope.$broadcast(VnoService.VNO_CHANGED, undefined);
            });
        }
        
        private cleanServiceData(): void {
            this.vnos = [];
        }
    }
}
